import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FiArrowRight, FiExternalLink } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './Insights.css';

const Insights = () => {
  const { t, i18n } = useTranslation('common');
  const lang = i18n.language || 'en';
  const [news, setNews] = useState([]);
  const [pressRefs, setPressRefs] = useState([]);
  const [activeTab, setActiveTab] = useState('articles');
  const [searchParams, setSearchParams] = useSearchParams();
  const type = searchParams.get('type') || 'all';

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const url = type === 'all' ? `/api/news?lang=${lang}` : `/api/news?type=${type}&lang=${lang}`;
        const res = await axios.get(url);
        setNews(res.data.data || []);
      } catch (error) {
        console.error('Error fetching news:', error);
      }
    };
    fetchNews();
  }, [type, lang]);

  useEffect(() => {
    const fetchPressRefs = async () => {
      try {
        const res = await axios.get(`/api/press-references?lang=${lang}`);
        setPressRefs(res.data.data || []);
      } catch (error) {
        console.error('Error fetching press references:', error);
      }
    };
    fetchPressRefs();
  }, [lang]);

  const types = ['all', 'blog', 'news', 'report'];

  const CATEGORY_LABELS = {
    en:      { 'market-intelligence': 'Market Intelligence', 'digital-commerce': 'Digital Commerce', 'digital-marketing': 'Digital Marketing', 'technology': 'Technology', 'operations': 'Operations', 'general': 'General' },
    'zh-CN': { 'market-intelligence': '市场情报', 'digital-commerce': '数字商业', 'digital-marketing': '数字营销', 'technology': '科技', 'operations': '运营', 'general': '综合' },
    'zh-TW': { 'market-intelligence': '市場情報', 'digital-commerce': '數字商業', 'digital-marketing': '數字行銷', 'technology': '科技', 'operations': '營運', 'general': '綜合' },
    ja:      { 'market-intelligence': 'マーケットインテリジェンス', 'digital-commerce': 'デジタルコマース', 'digital-marketing': 'デジタルマーケティング', 'technology': 'テクノロジー', 'operations': 'オペレーション', 'general': '一般' },
  };
  const catLabels = CATEGORY_LABELS[lang] || CATEGORY_LABELS.en;

  return (
    <div className="insights-page">
      <section className="page-hero">
        <div className="container">
          <p className="page-label">{t('nav.ourInsights')}</p>
          <h1>{t('nav.menu.insights.title')}</h1>
          <p className="page-description">{t('nav.menu.insights.description')}</p>
        </div>
      </section>

      {/* Tab switcher: Our Articles vs External References */}
      <section className="section">
        <div className="container">
          <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', borderBottom: '2px solid #e2e8f0' }}>
            <button
              className={`filter-btn ${activeTab === 'articles' ? 'active' : ''}`}
              onClick={() => setActiveTab('articles')}
              style={{ borderBottom: activeTab === 'articles' ? '2px solid #3b82f6' : '2px solid transparent', marginBottom: '-2px' }}
            >
              {lang === 'zh-CN' ? '我们的洞察' : lang === 'zh-TW' ? '我們的洞察' : lang === 'ja' ? '当社のインサイト' : 'Our Insights'}
            </button>
            <button
              className={`filter-btn ${activeTab === 'press' ? 'active' : ''}`}
              onClick={() => setActiveTab('press')}
              style={{ borderBottom: activeTab === 'press' ? '2px solid #3b82f6' : '2px solid transparent', marginBottom: '-2px' }}
            >
              {lang === 'zh-CN' ? '媒体与研究参考' : lang === 'zh-TW' ? '媒體與研究參考' : lang === 'ja' ? 'メディア・リサーチ参考' : 'Media & Research References'}
            </button>
          </div>

          {/* Our Articles */}
          {activeTab === 'articles' && (
            <>
              <div className="filter-bar">
                {types.map((typeItem) => (
                  <button
                    key={typeItem}
                    className={`filter-btn ${type === typeItem ? 'active' : ''}`}
                    onClick={() => setSearchParams(typeItem === 'all' ? {} : { type: typeItem })}
                  >
                    {typeItem.charAt(0).toUpperCase() + typeItem.slice(1)}
                  </button>
                ))}
              </div>
              <div className="insights-grid">
                {news.map((item, index) => (
                  <article key={item._id || index} className="insight-card">
                    <div className="insight-content">
                      <div className="insight-meta">
                        <span className="type-tag">{item.type}</span>
                        <span className="date">
                          {new Date(item.publishedAt).toLocaleDateString(
                            lang === 'ja' ? 'ja-JP' : lang.startsWith('zh') ? 'zh-CN' : 'en-US',
                            { month: 'short', day: 'numeric', year: 'numeric' }
                          )}
                        </span>
                      </div>
                      <h3>{item.title}</h3>
                      <p>{item.excerpt}</p>
                      {item.externalUrl ? (
                        <a href={item.externalUrl} target="_blank" rel="noopener noreferrer" className="read-more">
                          {lang === 'zh-CN' ? '阅读全文' : lang === 'zh-TW' ? '閱讀全文' : lang === 'ja' ? '全文を読む' : 'Read More'} <FiExternalLink />
                        </a>
                      ) : (
                        <Link to={`/insights/${item.slug}`} className="read-more">
                          {lang === 'zh-CN' ? '阅读全文' : lang === 'zh-TW' ? '閱讀全文' : lang === 'ja' ? '全文を読む' : 'Read More'} <FiArrowRight />
                        </Link>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            </>
          )}

          {/* Press References */}
          {activeTab === 'press' && (
            <div className="press-refs-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
              {pressRefs.map((ref, index) => (
                <article key={ref._id || index} style={{ background: '#fff', borderRadius: '10px', border: '1px solid #e2e8f0', padding: '20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <span style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{ref.publication?.name}</span>
                    <span style={{ fontSize: '11px', background: '#f0f9ff', color: '#0369a1', padding: '2px 8px', borderRadius: '9999px' }}>{catLabels[ref.category] || ref.category}</span>
                  </div>
                  <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#0f172a', margin: 0, lineHeight: '1.4' }}>{ref.articleTitle}</h3>
                  <p style={{ fontSize: '13px', color: '#475569', margin: 0, lineHeight: '1.6' }}>{ref.summary}</p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto' }}>
                    <span style={{ fontSize: '12px', color: '#94a3b8' }}>{ref.publishedDate}</span>
                    <a href={ref.articleUrl} target="_blank" rel="noopener noreferrer"
                      style={{ fontSize: '13px', color: '#3b82f6', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 500 }}>
                      {lang === 'zh-CN' ? '阅读原文' : lang === 'zh-TW' ? '閱讀原文' : lang === 'ja' ? '元記事を読む' : 'Read Article'} <FiExternalLink size={12} />
                    </a>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Insights;
